import { formatMonthYear } from '@/lib/format'
import type { MonthKey } from '@/lib/planning-types'

const SHORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']

function parts(month: MonthKey): [number, number] {
  const [year, index] = month.split('-')
  return [Number(year), Number(index)]
}

/** "September 2026" */
export function monthLabel(month: MonthKey): string {
  const [year, index] = parts(month)
  return formatMonthYear(index, year)
}

/** "sep" in het lopende jaar, "sep '27" zodra het venster over de jaargrens loopt. */
export function monthShort(month: MonthKey, referenceYear?: number): string {
  const [year, index] = parts(month)
  const short = SHORT[index - 1]
  if (referenceYear !== undefined && year !== referenceYear) return `${short} '${String(year).slice(2)}`
  return short
}

export function monthYear(month: MonthKey): number {
  return parts(month)[0]
}

/** "Augustus t/m oktober 2026", of "December 2026 t/m februari 2027" over de jaargrens. */
export function monthRangeLabel(months: MonthKey[]): string {
  if (months.length === 0) return ''
  const first = months[0]
  const last = months[months.length - 1]
  if (first === last) return monthLabel(first)

  const sameYear = monthYear(first) === monthYear(last)
  const head = sameYear ? monthLabel(first).replace(` ${monthYear(first)}`, '') : monthLabel(first)

  return `${head} t/m ${monthLabel(last).toLowerCase()}`
}

export function currentMonthKey(today = new Date()): MonthKey {
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
}

export function shiftMonth(month: MonthKey, delta: number): MonthKey {
  const [year, index] = parts(month)
  const date = new Date(year, index - 1 + delta, 1)
  return currentMonthKey(date)
}

/** Hoeveel maanden `to` na `from` ligt; negatief als hij ervoor valt. */
export function monthsBetween(from: MonthKey, to: MonthKey): number {
  const [fromYear, fromIndex] = parts(from)
  const [toYear, toIndex] = parts(to)
  return (toYear - fromYear) * 12 + (toIndex - fromIndex)
}

/** De maanden van `from` tot en met `to`; omgekeerd meegegeven is een lege reeks. */
export function monthRange(from: MonthKey, to: MonthKey): MonthKey[] {
  const span = monthsBetween(from, to)
  if (span < 0) return []

  return Array.from({ length: span + 1 }, (_, step) => shiftMonth(from, step))
}
